import { CrmEntityStatus, CrmEntityType, CrmProspectUpdate } from "./crm.schema";

type CrmRecord = {
	id?: number | null;
	crmType?: string | null;
	syncStatus?: string | null;
	syncError?: string | null;
};

interface ProspectWithCrmRows {
	id: number;
	crmProspects?: CrmRecord[] | null;
	crmDeals?: CrmRecord[] | null;
	crmLeads?: CrmRecord[] | null;
	organization?: {
		crmOrganizations?: CrmRecord[] | null;
	} | null;
}

interface ListPersonWithProspect {
	prospect?: ProspectWithCrmRows | null;
}

const normalizeRecord = (record: CrmRecord, update: CrmProspectUpdate): CrmRecord => ({
	...record,
	...(update.status === CrmEntityStatus.PENDING && !record.syncStatus
		? { syncStatus: CrmEntityStatus.PENDING }
		: {}),
	...(update.status === CrmEntityStatus.FAILED && update.error && !record.syncError
		? { syncError: update.error }
		: {}),
});

const upsertById = (records: CrmRecord[] | null | undefined, record: CrmRecord): CrmRecord[] => {
	const nextRecords = [...(records ?? [])];

	if (record.id === null || record.id === undefined) {
		const providerIndex = record.crmType
			? nextRecords.findIndex((existingRecord) =>
					recordMatchesProvider(existingRecord, record.crmType!)
				)
			: -1;

		if (providerIndex === -1) {
			return [...nextRecords, record];
		}

		nextRecords[providerIndex] = { ...nextRecords[providerIndex], ...record };

		return nextRecords;
	}

	const index = nextRecords.findIndex((existingRecord) => existingRecord.id === record.id);

	if (index === -1) {
		return [...nextRecords, record];
	}

	nextRecords[index] = { ...nextRecords[index], ...record };

	return nextRecords;
};

const recordMatchesProvider = (record: CrmRecord, provider: string): boolean =>
	String(record.crmType ?? "")
		.toLowerCase()
		.includes(provider.toLowerCase());

const markFailed = (
	records: CrmRecord[] | null | undefined,
	provider: string,
	error?: string
): CrmRecord[] | null | undefined => {
	if (!records?.length || !error) {
		return records;
	}

	const hasProviderMatch = records.some((record) => recordMatchesProvider(record, provider));

	return records.map((record) => {
		if (!hasProviderMatch && records.length !== 1) {
			return record;
		}

		if (hasProviderMatch && !recordMatchesProvider(record, provider)) {
			return record;
		}

		return { ...record, syncStatus: "ERROR", syncError: error };
	});
};

export const applyCrmUpdateToProspect = (
	prospect: ProspectWithCrmRows | null | undefined,
	update: CrmProspectUpdate
) => {
	if (!prospect || prospect.id !== update.prospectId) {
		return;
	}

	if (update.entity === CrmEntityType.LEAD) {
		if (update.crmLead) {
			prospect.crmLeads = upsertById(prospect.crmLeads, normalizeRecord(update.crmLead, update));

			return;
		}

		if (update.crmProspect) {
			prospect.crmProspects = upsertById(
				prospect.crmProspects,
				normalizeRecord(update.crmProspect, update)
			);

			return;
		}

		if (update.status === CrmEntityStatus.FAILED) {
			prospect.crmLeads = markFailed(prospect.crmLeads, update.provider, update.error);
			prospect.crmProspects = markFailed(prospect.crmProspects, update.provider, update.error);
		}
	}

	if (update.entity === CrmEntityType.CONTACT) {
		if (update.crmProspect) {
			prospect.crmProspects = upsertById(
				prospect.crmProspects,
				normalizeRecord(update.crmProspect, update)
			);

			return;
		}

		if (update.status === CrmEntityStatus.FAILED) {
			prospect.crmProspects = markFailed(prospect.crmProspects, update.provider, update.error);
		}
	}

	if (update.entity === CrmEntityType.ACCOUNT) {
		if (!prospect.organization) {
			return;
		}

		if (update.crmOrganization) {
			prospect.organization.crmOrganizations = upsertById(
				prospect.organization.crmOrganizations,
				normalizeRecord(update.crmOrganization, update)
			);

			return;
		}

		if (update.status === CrmEntityStatus.FAILED) {
			prospect.organization.crmOrganizations = markFailed(
				prospect.organization.crmOrganizations,
				update.provider,
				update.error
			);
		}
	}

	if (update.entity === CrmEntityType.DEAL) {
		if (update.crmDeal) {
			prospect.crmDeals = upsertById(prospect.crmDeals, normalizeRecord(update.crmDeal, update));

			return;
		}

		if (update.status === CrmEntityStatus.FAILED) {
			prospect.crmDeals = markFailed(prospect.crmDeals, update.provider, update.error);
		}
	}
};

export const applyCrmUpdateToListPersons = (
	listPersons: ListPersonWithProspect[],
	update: CrmProspectUpdate
) => {
	for (const listPerson of listPersons) {
		applyCrmUpdateToProspect(listPerson.prospect, update);
	}
};
